import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags.js'
import * as nodes from '/vercel/path0/markdoc/nodes/index.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Pricing\ndescription: Choose the plan that fits the needs of your solution. For testing or for private purposes and up to 100 credits per month you may use our services for free.\n---\n\n## Services and Credits\n\nPricing is based on the use of credits. Credits are counted by the number of requests or the duration of open connections, whereas each service requires different numbers of credits per unit.\n\n{% pricing-services-table /%}\n\n## Prices of our Plans\n\nFor testing or for private purposes and up to 100 credits per month you may use our services for free. For productive use, or if you consume more than 100 credits, you have to subscribe to one of our commercial plans. The plans include different numbers of credits and offer different discounts for additional credits.\n\n{% pricing-prices-table /%}\n\n## Frequently Asked Questions\n\n**How can I try your services?**  \nSimply create a free account and start developing!\n\n**How can I order a commercial plan?**  \nDrop us a mail at support@geops.io. You  will first receive a quote. After confirmation, you will get an invoice that can be paid by credit card or bank transfer. Invoices can be issued monthly or for a complete year in advance. If we have to charge credits exceeding the limits of your plan, we will do so monthly.\n\n**Can I change my plan?**  \nYes, of course. You can change your plan at the end of each billing month. If you have been billed on a yearly basis we will let you upgrade your subscription at any time.\n\n**How can I cancel?**  \nDrop us a mail at support@geops.io and we will terminate your subscription at the end of the billing period.\n\n**Which is the right plan for me?**  \nWe know it is difficult to choose the right plan for your specific use case. To give you an idea, the following two scenarios exemplify the costs you may expect.\n\n_Scenario 1_  \nAs a transit agency you offer to your customer a map that displays the courses of a trip your customer has chosen. A trip will on average consist of three different connections. The map has a medium size of 600 x 800 pixel, so each view will require around twelve requests for the map tiles. The map functionality is used by 3000 visitors per month, and each user pans and zooms the map two or three times.\n\nIn this scenario you will need approx. 500 credits, so you will stay well within the limits of the Basic plan.\n\n_Scenario 2_  \nAs a transit operator you would like to show to your visitors all vehicles from your company following your line network. You implement the map full screen and use the real-time service to show the vehicles on it. Your website has only 1000 visitors per month. However most of them are fascinated by your map, so they keep it open for five minutes in average.\n\nThe large map will need 100 requests for each map view. If all of your visitors zoom and pan the map, each of them will easily cause 1000 requests, resulting in a total of 3000 credits per month for all users. Additionally, the usage of the real-time service requires another 100 credits. With this kind of usage you could still go with the Basic plan and pay the additional requests separately. However, if the usage of your site increases only slightly, you may want to consider getting the Standard plan.\n\n**How can I calculate how many credits my specific application requires?**  \nOur [cost calculator](/costcalculator) should be helpful to get an idea on this.\n\n**What happens if I use more credits than my plan provides?**  \nIf you are using the free plan and surpass the free limits, we will inform you by mail, and we take the right to limit your usage of our services at any time. However you will never be charged without warning.\n\nIf you are on a paid plan, we will also inform you by mail when you reach the limit of credits included within your plan. Within the paid plans, all credits exceeding your plan will be charged automatically.\n\n**Where can I see, how many credits I have been using?**  \nYou may always check current usage of credits in the Keys section of your account.\n";
const filepath = "/pricing.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
